import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    CircularProgress,
    Alert
} from '@mui/material';
import ApplicantService from '../../services/ApplicantService';
import { ApplicantInfo } from '../../models/Applicant';

interface ProfileInfoDialogProps {
    open: boolean;
    onClose: () => void;
}

const formatPhoneNumber = (value: string) => {
    const digits = value.replace(/\D/g, '');

    if (digits.length <= 3) {
        return digits;
    }

    if (digits.length <= 6) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }

    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

const normalizePhoneNumber = (value: string) => value.replace(/\D/g, '');

const ProfileInfoDialog: React.FC<ProfileInfoDialogProps> = ({ open, onClose }) => {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            loadApplicantInfo();
        }
    }, [open]);

    const loadApplicantInfo = async () => {
        setLoading(true);
        setError('');
        setFirstName('');
        setLastName('');
        setPhoneNumber('');
        try {
            const service = new ApplicantService();
            const info = await service.getApplicant();
            setFirstName(info.first_name || '');
            setLastName(info.last_name || '');
            setPhoneNumber(info.phone_number ? formatPhoneNumber(info.phone_number) : '');
        } catch {
            // If no profile exists yet, just show blank fields
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!firstName.trim() || !lastName.trim()) {
            setError('First name and last name are required');
            return;
        }

        setSaving(true);
        setError('');
        const service = new ApplicantService();
        const payload: ApplicantInfo = {
            first_name: firstName.trim(),
            last_name: lastName.trim(),
        };

        const normalizedPhone = normalizePhoneNumber(phoneNumber);

        if (normalizedPhone) {
            payload.phone_number = normalizedPhone;
        }

        await service.createUpdateApplicant(payload);
        setSaving(false);
        onClose();
    };

    const handleClose = () => {
        setError('');
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Profile Info</DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box sx={{ pt: 1 }}>
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}
                        <TextField
                            label="First Name"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            fullWidth
                            required
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            label="Last Name"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            fullWidth
                            required
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            label="Phone Number"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(formatPhoneNumber(e.target.value))}
                            fullWidth
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={loading || saving}
                >
                    {saving ? <CircularProgress size={20} /> : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ProfileInfoDialog;
